import { lockScroll, unlockScroll } from '@theme/utilities';

class CustomHeaderDrawer extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';

    this.toggleButton = this.querySelector('[data-custom-drawer-toggle]');
    this.overlay = this.querySelector('[data-custom-drawer-overlay]');
    this.panel = this.querySelector('.custom-header-drawer__panel');
    this.closeButtons = this.querySelectorAll('[data-custom-drawer-close]');
    this.detailsElements = this.querySelectorAll('.custom-header-drawer__details');

    if (!(this.toggleButton instanceof HTMLButtonElement) || !(this.overlay instanceof HTMLElement)) return;

    this.overlayParent = this.overlay.parentElement;
    this.closeTimeout = null;

    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.handleTransitionEnd = this.handleTransitionEnd.bind(this);
    this.handlePanelClick = this.handlePanelClick.bind(this);
    this.handleDetailsToggle = this.handleDetailsToggle.bind(this);
    this.handleSummaryClick = this.handleSummaryClick.bind(this);

    this.toggleButton.addEventListener('click', this.handleToggleClick);
    this.closeButtons.forEach(function (button) {
      button.addEventListener('click', this.handleToggleClick);
    }, this);
    document.addEventListener('keyup', this.handleKeyUp);

    if (this.panel instanceof HTMLElement) {
      this.panel.addEventListener('click', this.handlePanelClick);
    }

    this.detailsElements.forEach(function (details) {
      details.addEventListener('toggle', this.handleDetailsToggle);
      var summary = details.querySelector('summary');
      if (summary instanceof HTMLElement) {
        summary.addEventListener('click', this.handleSummaryClick);
      }
    }, this);
  }

  disconnectedCallback() {
    if (this.closeTimeout) {
      window.clearTimeout(this.closeTimeout);
    }
    if (this.isOpen) {
      this.restoreOverlay();
      unlockScroll(this);
    }
    document.removeEventListener('keyup', this.handleKeyUp);
    if (this.panel instanceof HTMLElement) {
      this.panel.removeEventListener('click', this.handlePanelClick);
    }
    this.detailsElements.forEach(function (details) {
      details.removeEventListener('toggle', this.handleDetailsToggle);
      var summary = details.querySelector('summary');
      if (summary instanceof HTMLElement) {
        summary.removeEventListener('click', this.handleSummaryClick);
      }
    }, this);
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get openSubmenus() {
    return Array.from(this.querySelectorAll('.custom-header-drawer__details[open]'));
  }

  getSlidePanel(details) {
    if (!(details instanceof HTMLDetailsElement)) return null;
    var panel = details.querySelector(':scope > .custom-header-drawer__slide-panel');
    return panel instanceof HTMLElement ? panel : null;
  }

  mountOverlay() {
    if (!(this.overlay instanceof HTMLElement)) return;
    document.body.appendChild(this.overlay);
  }

  restoreOverlay() {
    if (!(this.overlay instanceof HTMLElement) || !(this.overlayParent instanceof HTMLElement)) return;
    this.overlayParent.appendChild(this.overlay);
  }

  finishClose() {
    if (this.isOpen) return;
    this.overlay.setAttribute('hidden', '');
    this.restoreOverlay();
    this.resetSubmenus();
  }

  resetSubmenus() {
    this.openSubmenus.forEach(function (details) {
      details.removeAttribute('open');
    });
    this.querySelectorAll('.custom-header-drawer__slide-panel.is-active').forEach(function (panel) {
      panel.classList.remove('is-active');
    });
  }

  closeSiblingDetails(currentDetails) {
    if (!(currentDetails instanceof HTMLDetailsElement)) return;

    var parentMenu = currentDetails.parentElement?.closest('.custom-header-drawer__menu');
    if (!(parentMenu instanceof HTMLElement)) return;

    parentMenu.querySelectorAll(':scope > .custom-header-drawer__item > .custom-header-drawer__details[open]').forEach(
      function (details) {
        if (details !== currentDetails) {
          this.closeDetailsAnimated(details);
        }
      },
      this
    );
  }

  openDetailsAnimated(details) {
    var panel = this.getSlidePanel(details);
    if (!(panel instanceof HTMLElement)) return;

    panel.classList.remove('is-active');
    panel.style.transform = 'translate3d(100%, 0, 0)';
    panel.style.transition = 'none';
    panel.offsetHeight;
    panel.style.transition = '';

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        panel.classList.add('is-active');
        panel.style.transform = '';
      });
    });
  }

  closeDetailsAnimated(details, callback) {
    var panel = this.getSlidePanel(details);
    if (!(panel instanceof HTMLElement) || this.prefersReducedMotion) {
      details.removeAttribute('open');
      panel?.classList.remove('is-active');
      if (typeof callback === 'function') callback();
      return;
    }

    var finish = function (event) {
      if (event.target !== panel || event.propertyName !== 'transform') return;
      panel.removeEventListener('transitionend', finish);
      details.removeAttribute('open');
      panel.classList.remove('is-active');
      if (typeof callback === 'function') callback();
    };

    panel.addEventListener('transitionend', finish);
    panel.classList.remove('is-active');
  }

  closeTopSubmenu() {
    var openSubmenus = this.openSubmenus;
    if (openSubmenus.length === 0) return false;

    this.closeDetailsAnimated(openSubmenus[openSubmenus.length - 1]);
    return true;
  }

  handleSummaryClick(event) {
    var summary = event.currentTarget;
    if (!(summary instanceof HTMLElement)) return;

    var details = summary.closest('.custom-header-drawer__details');
    if (details instanceof HTMLDetailsElement && details.open) {
      event.preventDefault();
    }
  }

  handleDetailsToggle(event) {
    var details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;

    if (details.open) {
      this.closeSiblingDetails(details);
      this.openDetailsAnimated(details);
      return;
    }

    var panel = this.getSlidePanel(details);
    panel?.classList.remove('is-active');
  }

  handlePanelClick(event) {
    if (!(event.target instanceof Element)) return;

    var closeButton = event.target.closest('[data-submenu-close]');
    if (!(closeButton instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    var details = closeButton.closest('.custom-header-drawer__details');
    if (details instanceof HTMLDetailsElement) {
      this.closeDetailsAnimated(details);
    }
  }

  handleTransitionEnd(event) {
    if (!(this.panel instanceof HTMLElement)) return;
    if (event.target !== this.panel || event.propertyName !== 'transform') return;
    if (this.closeTimeout) {
      window.clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    this.panel.removeEventListener('transitionend', this.handleTransitionEnd);
    this.finishClose();
  }

  open() {
    this.classList.add('is-open');
    this.toggleButton.setAttribute('aria-expanded', 'true');
    this.mountOverlay();
    this.overlay.removeAttribute('hidden');

    if (this.prefersReducedMotion) {
      this.overlay.classList.add('is-open');
      lockScroll(this);
      return;
    }

    this.overlay.classList.remove('is-open');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.overlay.classList.add('is-open');
      });
    });
    lockScroll(this);
  }

  close() {
    this.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    this.toggleButton.setAttribute('aria-expanded', 'false');
    unlockScroll(this);

    if (this.prefersReducedMotion) {
      this.finishClose();
      return;
    }

    if (this.panel instanceof HTMLElement) {
      this.panel.addEventListener('transitionend', this.handleTransitionEnd);
      this.closeTimeout = window.setTimeout(() => {
        this.panel.removeEventListener('transitionend', this.handleTransitionEnd);
        this.finishClose();
      }, 400);
      return;
    }

    this.finishClose();
  }

  handleToggleClick() {
    if (this.isOpen) {
      this.close();
      return;
    }
    this.open();
  }

  handleKeyUp(event) {
    if (!(event instanceof KeyboardEvent)) return;
    if (event.key !== 'Escape' || !this.isOpen) return;

    if (this.closeTopSubmenu()) return;

    this.close();
  }
}

if (!customElements.get('custom-header-drawer')) {
  customElements.define('custom-header-drawer', CustomHeaderDrawer);
}
